/**
 * Правила канала → поведение строки ввода.
 *
 * Правила отдаёт наш Chatwoot (`GET inboxes/:id/unia_capabilities`, Unia::ChannelCapabilities,
 * _vendor/chatwoot/_giper/CHANNEL_CAPABILITIES.md): есть ли в канале голосовые, какие файлы он берёт,
 * несколько ли за раз, можно ли подпись, предел размера. Здесь — чистое превращение этих правил в то,
 * что показывает и делает строка ввода (проверяется без экрана: channelRules.test.ts).
 *
 * Нет правил (старый сервер без адреса) — поведение как до них: голос можно, любые файлы до 40 МБ,
 * одним сообщением, запись с iPhone превращается в MP3 на телефоне.
 */
import { fileKind, MAX_FILE_BYTES, MAX_FILES, type FileKind } from '@/lib/attachments';

/** Ответ сервера (Unia::ChannelCapabilities). */
export interface ChannelCapabilities {
  voice: 'voice' | 'text_only';
  voice_format: 'ogg' | 'm4a' | 'mp3' | null;
  attachments: Record<FileKind, boolean>;
  multiple_attachments: boolean;
  caption_with_attachment: boolean;
  max_attachment_mb: number;
  max_mb_by_kind?: Partial<Record<FileKind, number>>;
  emoji: boolean;
  /** Канал может начать переписку с нуля (ник / телефон / почта). Старый сервер поля не знает. */
  new_chat?: boolean;
  /** Настоящая пересылка внутри канала («Переслано от…», можно скрыть отправителя). */
  native_forward?: boolean;
  /** Кружок (видеосообщение) доходит кружком. */
  video_note?: boolean;
}

export interface ComposerRules {
  /** Кнопка «Голосом» после записи. Нет — только «Текстом». */
  voiceAsAudio: boolean;
  /** Пояснение под записью, почему голосом нельзя. */
  voiceHint: string | null;
  /** Пункты листа «Прикрепить». */
  sheet: { media: boolean; camera: boolean; file: boolean };
  /** accept для «Фото или видео» / «Камера» и для «Файл» (пусто — без ограничения). */
  mediaAccept: string;
  fileAccept: string;
  /** Скрепка вообще видна. */
  canAttach: boolean;
  /** Какие виды файлов канал принимает. */
  kinds: Record<FileKind, boolean>;
  /** Предел файла данного вида, байты. */
  maxBytes: (kind: FileKind) => number;
  maxFiles: number;
  /** Канал берёт одно вложение на сообщение — каждый файл уходит своим сообщением. */
  splitFiles: boolean;
  /** Подпись в одном сообщении с файлом; нет — текст уходит отдельным сообщением. */
  captionWithFiles: boolean;
  /** Сервер сам приводит голосовое к формату канала — запись уходит как есть, без MP3 на телефоне. */
  serverNormalizes: boolean;
}

const ALL: Record<FileKind, boolean> = { image: true, video: true, audio: true, file: true };

/** «В почте голосовые не отправляются» — по-человечески для каждого канала без голосовых. */
export function voiceHintFor(channelType?: string | null): string {
  const t = (channelType ?? '').replace('Channel::', '');
  const where: Record<string, string> = {
    Email: 'В почте',
    Sms: 'В СМС',
    TwilioSms: 'В СМС',
    Line: 'В LINE',
    Tiktok: 'В TikTok',
    TwitterProfile: 'В X',
  };
  return `${where[t] ?? 'В этом канале'} голосовые не отправляются — только текстом`;
}

export function composerRules(caps: ChannelCapabilities | null | undefined, channelType?: string | null): ComposerRules {
  if (!caps) {
    return {
      voiceAsAudio: true, voiceHint: null,
      sheet: { media: true, camera: true, file: true },
      mediaAccept: 'image/*,video/*', fileAccept: '',
      canAttach: true, kinds: ALL,
      maxBytes: () => MAX_FILE_BYTES, maxFiles: MAX_FILES,
      splitFiles: false, captionWithFiles: true, serverNormalizes: false,
    };
  }
  const kinds = { ...ALL, ...caps.attachments };
  const media = [kinds.image && 'image/*', kinds.video && 'video/*'].filter(Boolean).join(',');
  const textOnly = caps.voice === 'text_only';
  const mb = (kind: FileKind) => caps.max_mb_by_kind?.[kind] ?? caps.max_attachment_mb ?? MAX_FILE_BYTES / 1024 / 1024;
  return {
    voiceAsAudio: !textOnly,
    voiceHint: textOnly ? voiceHintFor(channelType) : null,
    sheet: { media: media !== '', camera: media !== '', file: kinds.file || kinds.audio },
    mediaAccept: media,
    // Документы можно — без ограничения (остальное отсеет проверка вида); только аудио — только аудио.
    fileAccept: kinds.file ? '' : 'audio/*',
    canAttach: kinds.image || kinds.video || kinds.audio || kinds.file,
    kinds,
    maxBytes: (kind) => Math.min(mb(kind) * 1024 * 1024, MAX_FILE_BYTES),
    maxFiles: MAX_FILES,
    splitFiles: !caps.multiple_attachments,
    captionWithFiles: caps.caption_with_attachment,
    serverNormalizes: true,
  };
}

const KIND_RU: Record<FileKind, string> = { image: 'картинки', video: 'видео', audio: 'аудио', file: 'документы' };

/** Почему файл не подходит каналу (null — подходит). */
export function rejectReason(file: { type: string; size: number }, rules: ComposerRules): string | null {
  const kind = fileKind(file);
  if (!rules.kinds[kind]) return `этот канал не принимает ${KIND_RU[kind]}`;
  const max = rules.maxBytes(kind);
  if (file.size > max) return `больше ${Math.round(max / 1024 / 1024)} МБ — предел этого канала`;
  return null;
}

/** Одно исходящее сообщение: файлы (может быть пусто — только текст) и текст. */
export interface OutgoingPart { files: File[]; caption: string }

/**
 * Как разложить файлы и подпись по сообщениям — как панель Chatwoot для WhatsApp/Instagram:
 *  • канал берёт несколько файлов и подпись — одно сообщение;
 *  • одно вложение на сообщение — каждый файл своим сообщением, подпись только к первому;
 *  • подпись к файлу нельзя (Instagram, TikTok) — файлы без подписи, текст отдельным сообщением после них.
 */
export function planFileMessages(files: File[], caption: string, rules: ComposerRules): OutgoingPart[] {
  const text = caption.trim();
  if (!rules.splitFiles && rules.captionWithFiles) return [{ files, caption: text }];
  const groups = rules.splitFiles ? files.map((f) => [f]) : [files];
  const parts: OutgoingPart[] = groups.map((g, i) => ({ files: g, caption: rules.captionWithFiles && i === 0 ? text : '' }));
  if (!rules.captionWithFiles && text) parts.push({ files: [], caption: text });
  return parts;
}
